<template>
    <div class="dbresult">
        <div v-if="error" class="dbresult__error">{{ error }}</div>
        <div v-else-if="result" class="dbresult__body">
            <div class="dbresult__status">
                <template v-if="result.type === 'QUERY'">返回 {{ result.rowCount }} 行</template>
                <template v-else>执行成功，影响 {{ result.affectedRows }} 行</template>
                <span class="dbresult__sep">·</span>
                <span>耗时 {{ result.elapsedMs }} ms</span>
            </div>

            <el-alert v-for="(w, i) in (result.warnings || [])" :key="i" :title="w" type="warning" :closable="false"
                show-icon class="dbresult__warn" />

            <div v-if="result.type === 'QUERY' && (result.columns || []).length">
                <div class="dbresult__actions">
                    <el-button size="small" type="primary"
                        :disabled="selection.length === 0 || selection.length > maxSelect" @click="emitFavorite">
                        收藏所选行（{{ selection.length }}）
                    </el-button>
                    <span class="dbresult__tips">选中 1~100 行可收藏；需先保存该 SQL 块</span>
                </div>
                <div class="dbresult__toolbar">
                    <el-input v-model="keyword" class="dbresult__search" size="small" clearable
                        placeholder="搜索结果（关键字匹配所有列）">
                        <template #prefix>
                            <el-icon>
                                <Search />
                            </el-icon>
                        </template>
                    </el-input>
                    <el-button size="small" @click="openColumnSetting">
                        <el-icon class="el-icon--left">
                            <Setting />
                        </el-icon>
                        列设置
                    </el-button>
                    <span v-if="keyword" class="dbresult__match">匹配 {{ matchedRows }} / {{ totalRows }} 行</span>
                    <span class="dbresult__tips">点击表头可排序；要左右比对哪列，点「列设置」自己固定</span>
                </div>
                <!-- vxe-table：行数超过 20 自动启用虚拟滚动；排序/固定列均由用户在「列设置」里决定 -->
                <vxe-table ref="xTable" class="dbresult__table" :data="viewData" :max-height="300" size="small"
                    border stripe show-overflow :row-config="{ keyField: '__idx', isHover: true }"
                    :scroll-y="{ enabled: true, gt: 20 }" :sort-config="{ trigger: 'cell', orders: ['asc', 'desc', null] }"
                    @checkbox-change="onCheckboxChange" @checkbox-all="onCheckboxChange">
                    <vxe-column type="checkbox" width="44" />
                    <vxe-column type="seq" width="52" title="#" />
                    <vxe-column v-for="(col, ci) in result.columns" :key="ci" :field="'c' + ci" :title="col.name"
                        min-width="140" :sortable="true">
                        <template #default="{ row }">
                            <span v-if="row['c' + ci] === null" class="dbresult__null">NULL</span>
                            <span v-else>{{ row['c' + ci] }}</span>
                        </template>
                    </vxe-column>
                </vxe-table>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * 结果面板
 * <p>拆成独立子组件的目的：让「大结果表格」只在结果变化时重渲染，
 * 避免父级（如 SQL 输入框打字）的每次重渲染都连带重算表格。</p>
 */
export default {
    name: 'DbResultPanel',
    props: {
        // 结果对象（父级已 markRaw，避免深层响应式）
        result: { type: Object, default: null },
        error: { type: String, default: '' },
        maxSelect: { type: Number, default: 100 }
    },
    emits: ['favorite'],
    data() {
        return {
            selection: [],
            keyword: ''
        };
    },
    computed: {
        // 仅在 result 引用变化时重算（result 为 markRaw，不会被深层追踪）
        tableData() {
            if (!this.result || !this.result.columns) return [];
            return (this.result.rows || []).map((row, ri) => {
                const obj = { __idx: ri };
                row.forEach((cell, ci) => {
                    obj['c' + ci] = cell;
                });
                return obj;
            });
        },
        totalRows() {
            return this.tableData.length;
        },
        // 关键字跨所有列过滤（不区分大小写）
        viewData() {
            const key = this.keyword.trim().toLowerCase();
            if (!key) return this.tableData;
            const columns = this.result.columns || [];
            return this.tableData.filter(row => {
                for (let ci = 0; ci < columns.length; ci++) {
                    const cell = row['c' + ci];
                    if (cell !== null && cell !== undefined && String(cell).toLowerCase().includes(key)) {
                        return true;
                    }
                }
                return false;
            });
        },
        matchedRows() {
            return this.viewData.length;
        }
    },
    watch: {
        result() {
            // 结果整体替换，清掉搜索词与选中状态
            this.keyword = '';
            this.clearSelection();
        },
        keyword() {
            // 过滤后可能隐藏已选行，清空选中避免「看不到却被收藏」
            this.clearSelection();
        }
    },
    methods: {
        clearSelection() {
            this.selection = [];
            // 结果/筛选变化后，清掉 vxe-table 内部的行选中状态（keyField 相同可能残留误选）
            this.$nextTick(() => {
                const table = this.$refs.xTable;
                if (table && table.clearCheckboxRow) {
                    table.clearCheckboxRow();
                }
            });
        },
        // vxe-table 的 checkbox-change / checkbox-all 事件都会回传 records
        onCheckboxChange({ records }) {
            this.selection = records || [];
        },
        // 打开 vxe 自带的列设置面板：显隐、拖拽排序、左/右固定，全部由用户自己点
        openColumnSetting() {
            const table = this.$refs.xTable;
            if (table && table.openCustom) {
                table.openCustom();
            }
        },
        emitFavorite() {
            if (this.selection.length === 0 || this.selection.length > this.maxSelect) return;
            const columns = this.result.columns || [];
            const rows = this.selection.map(item => columns.map((col, ci) => item['c' + ci]));
            this.$emit('favorite', rows);
        }
    }
};
</script>

<!-- 抽屉与弹窗会挂到 body 下，样式放非 scoped 块并统一前缀 -->
<style>
.dbresult__error {
    padding: 8px 12px;
    border-radius: 8px;
    background-color: #fef0f0;
    color: #f56c6c;
    font-size: 13px;
}

.dbresult__status {
    font-size: 13px;
    color: #5b6a80;
}

.dbresult__sep {
    margin: 0 8px;
    color: #c0c4cc;
}

.dbresult__warn {
    margin-top: 8px;
}

.dbresult__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 10px 0 6px;
}

.dbresult__tips {
    font-size: 12px;
    color: #aab4c4;
}

.dbresult__toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 8px 0 6px;
}

.dbresult__search {
    width: 260px;
}

.dbresult__match {
    font-size: 12px;
    color: #7d8ba1;
}

.dbresult__null {
    color: #aab4c4;
    font-style: italic;
}
</style>
